import { FC } from 'react'
import en from 'src/constants/en'
import { AnalyticsFetchTopSourcesResponse } from 'src/services/__generated__/api'
import { PositivityLegend } from 'src/components/PositivityLegend'
import { useGetInformationTooltipsQuery } from 'src/reactQuery/useGetInformationTooltips'
import { SectionTitle } from 'src/components/SectionTitle'
import { Skeleton } from 'src/components/Skeleton'
import styles from './topSourcesSection.module.scss'
import { TopSourcesChart } from './TopSourcesChart'
import { calculateToTalMentions } from './utils/calculateToTalMentions'

// Cards are 212 wide with a 260px min-height and a 24px radius (see
// topSourcesChart.module.scss `.card`), laid out in a 16px-gap row. The section
// is 1170px wide with 24px side padding, so four cards fit across.
const CARD_WIDTH = 212
const CARD_HEIGHT = 260
const CARD_RADIUS = 24
const CARD_SKELETON_COUNT = 4

interface TopSourcesSectionProps {
  chartData?: AnalyticsFetchTopSourcesResponse
  isLoading?: boolean
}

export const TopSourcesSection: FC<TopSourcesSectionProps> = ({
  chartData,
  isLoading,
}) => {
  const { data: informationTooltips } = useGetInformationTooltipsQuery()

  const overallMentions =
    chartData?.reduce((mentionCount, mention) => {
      return calculateToTalMentions(mention) + mentionCount
    }, 0) || 0

  const hasData = Boolean(chartData?.length)

  // FIRST load only — nothing has arrived yet. This branch previously rendered
  // neither the chart row nor the empty state, so the section shrank to just
  // its title and jumped ~260px taller once the data landed.
  const isInitialLoading = Boolean(isLoading) && !chartData

  if (isInitialLoading) {
    return (
      <div className={styles.topSourcesSection}>
        <SectionTitle
          title={en.topSources}
          tooltipContent={informationTooltips?.topSources}
        />

        <div className={styles.chartWrapper}>
          {Array.from({ length: CARD_SKELETON_COUNT }).map((_, index) => (
            <Skeleton
              height={CARD_HEIGHT}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              radius={CARD_RADIUS}
              width={CARD_WIDTH}
            />
          ))}
        </div>

        {/* The legend is static chrome, not data. Rendering it here too means
            the footer height is reserved up front rather than appearing (and
            shifting the section) at the moment the cards resolve. */}
        <div className={styles.footer}>
          <PositivityLegend />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.topSourcesSection}>
      <SectionTitle
        isLoading={isLoading}
        title={en.topSources}
        tooltipContent={informationTooltips?.topSources}
      />

      {hasData ? (
        <>
          <div className={styles.chartWrapper}>
            {chartData?.map((chartDataItem) => {
              const { name, positives, negatives, neutrals } = chartDataItem

              return (
                <TopSourcesChart
                  key={`${name} - ${positives} - ${negatives} - ${neutrals}`}
                  mention={chartDataItem}
                  overallMentions={overallMentions}
                />
              )
            })}
          </div>

          <div className={styles.footer}>
            <PositivityLegend />
          </div>
        </>
      ) : (
        !isLoading && (
          <div className={styles.emptyState}>{en.notEnoughDataToDisplay}</div>
        )
      )}
    </div>
  )
}
